import {
	multiplySize,
	type GenericPosition,
	type GenericSize,
} from "@brickme/project-core/src/model/geom.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import { bitmapToImageData } from "@brickme/project-core/src/bitmap/image-data.ts";
import { renderPictureSync } from "@brickme/project-core/src/render/render-picture.ts";
import { runGeneratorSync } from "@brickme/project-core/src/utils/run-generator.ts";
import { ensureExhaustive } from "@brickme/project-core/src/utils/never.ts";
import {
	realWorldBrickSize,
	type PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import renderPictureAsPixels from "@brickme/project-core/src/render/render-picture-as-pixels.ts";
import renderBaseplateOutlines from "@brickme/project-core/src/render/render-baseplate-outlines.ts";
import renderDebugFaces from "@brickme/project-core/src/render/render-debug-faces.ts";
import type {
	RenderRunner,
	RenderSource,
	LoadedScene,
} from "./render-runner-type.ts";
import renderSource from "@brickme/project-core/src/render/render-source.ts";
import type { BrickSpriteCache } from "@brickme/project-core/src/render/brick-sprite-cache.ts";
import type { FaceBoundingBox } from "@brickme/project-core/src/model/project.ts";

// TODO: Find a better system for preventing unnecessary re-renders.
type CurrentRender = {
	readonly type: RenderSource["type"];
	// readonly drawBaseplates: boolean;
	// readonly picture: PictureConfiguration | undefined;
	// readonly build: BuildBitmap | undefined;
	// Not aborting any more
	// readonly abortController: AbortController;
};

type Options = {
	// In main thread will want timeout. But slower, so in worker prefer microtask
	readonly pendingRenderMethod: "timeout" | "microtask";
	// Called when a render throws. The worker has no Sentry of its own, so this is
	// how render failures escape the worker scope (the bridge forwards them to the
	// main-thread Sentry). Without it, a thrown render is silently swallowed and
	// the canvas is left on its opaque-black clear (see the catch in tryRender).
	readonly onError: (error: unknown) => void;
};

function createRenderRunner(
	options: Options,
): RenderRunner<HTMLCanvasElement | OffscreenCanvas> {
	let performingRender: CurrentRender | undefined;
	let canvasCtx:
		| OffscreenCanvasRenderingContext2D
		| CanvasRenderingContext2D
		| undefined;
	let utilityCanvasCtx: Parameters<typeof renderPictureSync>[2] | undefined;
	let canvasSourceImage: HTMLCanvasElement | OffscreenCanvas | undefined;
	let source: RenderSource | undefined;
	let faces:
		| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
		| undefined;
	let canvasSize: GenericSize<number> | undefined;
	let loadedScene: LoadedScene | undefined;
	let hasPendingRender = false;
	let drawBaseplates = false;
	let drawDebugFaces = false;
	// TODO: Rendering twice. Once on picture change, even though some render configurations
	// don't require it.
	let picture: PictureConfiguration | undefined;
	// Cross-render brick sprite cache, threaded between renders at a stable
	// brick size so sprites aren't re-rasterised every frame.
	let brickSpriteCache: BrickSpriteCache | undefined;

	function tryRender() {
		if (
			!canvasCtx ||
			!utilityCanvasCtx ||
			!source ||
			!canvasSize ||
			!canvasSourceImage ||
			!picture
		) {
			return;
		}

		// We let any current render continue to give quicker user feedback.
		// then pick up pending renders later. This solves the case of say scrubbing a slider.
		// If the slider emits an event every 10ms and render takes 15ms then we'll never see
		// a render until the user stops moving the slider.
		// currentRender.abortController.abort();
		if (performingRender) {
			hasPendingRender = true;
			return;
		}

		// We have a race condition where the following happens.
		// 1. picture changes
		// 2.
		//   - build starts
		//   - tell renderer of picture
		// 3. build finishes
		// 4. tell renderer of build
		// in 3 or 4 before the renderer is told of the new build, we have the new picture and
		// utility canvas. If the build doesn't match the picture, then just wait until we're
		// all in sync
		if (
			(source.type === "bricked" || source.type === "solid") &&
			(utilityCanvasCtx.canvas.width < source.build.width ||
				utilityCanvasCtx.canvas.height < source.build.height)
		) {
			return;
		}

		const myRender = {
			type: source.type,
			// drawBaseplates,
			// build: source.type === "bricked" ? source.build : undefined,
			// picture: drawBaseplates || source.type === "source" ? picture : undefined
		};
		performingRender = myRender;

		try {
			// Setting width or height (even if the same) results in a canvas clear
			// Note that this totally reset imageSmoothing settings
			const previousSmoothingEnabled = canvasCtx.imageSmoothingEnabled;
			const previousSmoothingQuality = canvasCtx.imageSmoothingQuality;
			if (canvasCtx.canvas.width !== canvasSize.width) {
				canvasCtx.canvas.width = canvasSize.width;
			}
			if (canvasCtx.canvas.height !== canvasSize.height) {
				canvasCtx.canvas.height = canvasSize.height;
			}
			if (canvasCtx.imageSmoothingEnabled !== previousSmoothingEnabled) {
				canvasCtx.imageSmoothingEnabled = previousSmoothingEnabled;
			}
			if (canvasCtx.imageSmoothingQuality !== previousSmoothingQuality) {
				canvasCtx.imageSmoothingQuality = previousSmoothingQuality;
			}

			if (import.meta.env.MODE === "development") {
				// eslint-disable-next-line no-console
				console.time(`Render (${myRender.type})`);
			}

			// Positioning
			let drawBounds:
				| (GenericSize<number> & GenericPosition<number>)
				| undefined;
			if (loadedScene) {
				// Positioning
				const realWorldPictureSizeMms = multiplySize(
					{
						width: picture.basePlateSize * picture.numberOfBasePlates.width,
						height: picture.basePlateSize * picture.numberOfBasePlates.height,
					},
					realWorldBrickSize.mm,
				);
				const destinationWidth =
					(canvasSize.width * realWorldPictureSizeMms.width) /
					loadedScene.scene.fullSize.realWorldSize.width.mm;
				const destinationHeight =
					(canvasSize.height * realWorldPictureSizeMms.height) /
					loadedScene.scene.fullSize.realWorldSize.height.mm;
				drawBounds = {
					x: Math.round(
						loadedScene.scene.fullSize.mountBoundsRatios.centrePosition.x *
							canvasSize.width -
							destinationWidth / 2,
					),
					y: Math.round(
						loadedScene.scene.fullSize.mountBoundsRatios.centrePosition.y *
							canvasSize.height -
							destinationHeight / 2,
					),
					width: Math.max(1, Math.round(destinationWidth)),
					height: Math.max(1, Math.round(destinationHeight)),
				};

				// Draw scene
				if (loadedScene.loadedImage) {
					canvasCtx.drawImage(
						loadedScene.loadedImage,
						0,
						0,
						canvasSize.width,
						canvasSize.height,
					);
				} else {
					canvasCtx.clearRect(0, 0, canvasSize.width, canvasSize.height);
				}

				// Draw a shadow
				const shadowOffset = 0.8;
				canvasCtx.fillStyle = "rgba(0, 0, 0, 0.5)";
				canvasCtx.fillRect(
					drawBounds.x + shadowOffset,
					drawBounds.y + shadowOffset,
					drawBounds.width,
					drawBounds.height,
				);
			}

			// Draw main image
			switch (source.type) {
				case "bricked":
					// Cross-render brick sprite cache: reused at a stable brick size
					// (panning, slider scrubbing, unrelated re-renders) so sprites
					// aren't re-rasterised every frame. renderPictureAsBricks guards
					// against caching a degenerate (empty getImageData readback)
					// sprite, so a one-off bad readback can't be pinned - it
					// re-rasterises and self-heals next frame. (Those empty readbacks
					// were caused by willReadFrequently on the utility context, now
					// removed.)
					brickSpriteCache = renderPictureSync(
						canvasCtx,
						source.build,
						utilityCanvasCtx,
						{ drawBounds, brickSpriteCache },
					);
					break;
				case "solid":
					runGeneratorSync(
						renderPictureAsPixels(canvasCtx, utilityCanvasCtx, source.build),
					);
					break;
				case "source":
					renderSource(canvasCtx, canvasSourceImage, picture);
					break;
				default:
					ensureExhaustive(source);
			}

			// Draw baseplates
			if (drawBaseplates) {
				renderBaseplateOutlines(
					canvasCtx,
					picture.numberOfBasePlates,
					canvasCtx.canvas,
				);
			}

			// Draw debug faces
			if (drawDebugFaces && faces !== undefined) {
				renderDebugFaces(
					canvasCtx,
					{ width: canvasSourceImage.width, height: canvasSourceImage.height },
					picture,
					faces,
				);
			}

			if (import.meta.env.MODE === "development") {
				// if (myRender.abortController.signal.aborted) {
				// 	console.log("Rendering ABORTED");
				// } else
				// eslint-disable-next-line no-console
				console.timeEnd(`Render (${myRender.type})`);
			}
		} catch (error) {
			// A thrown render (e.g. a device-specific canvas/GPU failure) must not be
			// swallowed. Previously performingRender stayed set, so every later render
			// bailed at the `if (performingRender)` guard and the canvas was left on
			// its opaque-black clear forever. We now always reset in `finally` so the
			// renderer can recover, and surface the error via onError (the worker
			// forwards it to the main-thread Sentry).
			// eslint-disable-next-line no-console
			console.error(`Render failed (${myRender.type})`, error);
			options.onError?.(error);
		} finally {
			if (performingRender === myRender) {
				performingRender = undefined;
			}

			if (hasPendingRender) {
				hasPendingRender = false;
				// Use next frame instead of direct call to avoid stack overflows
				tryRenderNextFrame();
			}
		}
	}

	let nextFrameRenderId: ReturnType<typeof setTimeout> | undefined;
	function tryRenderNextFrame() {
		// queueMicrotask happens too quick. No time to cancel before redundant call comes in.
		// This is part of multiple safeguards to try and prevent too many renders.
		// if (pendingRenderMethod === "microtask") {
		// queueMicrotask(() => void tryRender());
		// } else {
		if (nextFrameRenderId !== undefined) {
			clearTimeout(nextFrameRenderId);
		}
		nextFrameRenderId = setTimeout(tryRender);
	}

	return {
		setSourceImage(
			sourceImage: Bitmap,
			utilityCanvas: HTMLCanvasElement | OffscreenCanvas,
		) {
			/* eslint-disable no-param-reassign */
			utilityCanvas.width = sourceImage.width;
			utilityCanvas.height = sourceImage.height;
			/* eslint-enable no-param-reassign */
			canvasSourceImage = utilityCanvas;
			const sourceImageCtx = utilityCanvas.getContext("2d", {
				alpha: false,
			});
			if (sourceImageCtx === null) {
				throw new Error("Failed to get context");
			}
			sourceImageCtx.putImageData(bitmapToImageData(sourceImage), 0, 0);
			tryRenderNextFrame();
		},
		setCanvas(newCanvas: HTMLCanvasElement | OffscreenCanvas) {
			const newCtx = newCanvas.getContext("2d", {
				alpha: false,
			});
			if (newCtx === null) {
				throw new Error("Canvas ctx not working");
			}
			// Doesn't make a difference in createor app v2
			newCtx.imageSmoothingEnabled = false;
			canvasCtx = newCtx;
			tryRenderNextFrame();
		},
		setSource(newSource: RenderSource | undefined) {
			source = newSource;
			tryRenderNextFrame();
		},
		setFaces(
			newFaces:
				| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
				| undefined,
		) {
			faces = newFaces;
			tryRenderNextFrame();
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			if (newDrawBaseplates !== drawBaseplates) {
				drawBaseplates = newDrawBaseplates;
				tryRenderNextFrame();
			}
		},
		setDrawDebugFaces(newDrawDebugFaces: boolean) {
			if (newDrawDebugFaces !== drawDebugFaces) {
				drawDebugFaces = newDrawDebugFaces;
				tryRenderNextFrame();
			}
		},
		setLoadedScene(newLoadedScene: LoadedScene) {
			if (newLoadedScene !== loadedScene) {
				loadedScene = newLoadedScene;
				tryRenderNextFrame();
			}
		},
		setPictureConfiguration(newPicture: PictureConfiguration) {
			picture = newPicture;
			// Only source renders or base plates need picture config
			if (source?.type === "source" || drawBaseplates) {
				tryRenderNextFrame();
			}
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement | OffscreenCanvas,
			newCanvasSize: GenericSize<number>,
		) {
			canvasSize = newCanvasSize;
			const newCtx = newUtilityCanvas.getContext("2d", {
				alpha: false,
				// willReadFrequently: true
				// Deliberately NOT setting willReadFrequently. On Android Chrome an
				// OffscreenCanvas-in-worker 2D context created with willReadFrequently:true
				// falls onto a software-backed path whose getImageData intermittently
				// returns an all-black tile, so the brick sprite readback yields black
				// bricks / a black canvas (confirmed on a real Galaxy A23; master, which
				// never set the flag, renders fine on the same device via the default
				// accelerated backing). The flag was added for read perf but breaks
				// correctness here. If readback cost becomes a problem, eliminate the
				// getImageData round-trip instead (drawImage canvas->canvas blit).
			});
			if (newCtx === null) {
				throw new Error("Utility ctx not working");
			}
			utilityCanvasCtx = newCtx;
			tryRenderNextFrame();
		},
		clearUtilityCanvas() {
			canvasSize = undefined;
			utilityCanvasCtx = undefined;
			tryRenderNextFrame();
		},
		terminate() {
			// Nothing
		},
	};
}

export { createRenderRunner };
